import { useState } from "react";
import type { Transaction, RenameMerchantInput, UpdateTransactionInput } from "@money/shared";
import { formatCents, formatDate } from "../lib/format";

export function TransactionRow({
  tx,
  categories,
  onSave,
  onRename,
  onDelete,
}: {
  tx: Transaction;
  categories?: string[];
  onSave?: (patch: UpdateTransactionInput) => Promise<unknown>;
  onRename?: (input: RenameMerchantInput) => Promise<unknown>;
  onDelete?: (id: string) => void;
}) {
  const editable = !!onSave;
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(tx.category ?? "");
  const [notes, setNotes] = useState(tx.notes ?? "");
  const [displayName, setDisplayName] = useState(tx.display_name ?? "");
  const [busy, setBusy] = useState(false);

  const title = tx.display_name ?? tx.merchant_raw;
  const sign = tx.amount_cents < 0 ? "text-rose-300" : "text-emerald-300";
  const nameDirty = onRename && displayName !== (tx.display_name ?? "");
  const dirty = category !== (tx.category ?? "") || notes !== (tx.notes ?? "") || nameDirty;

  // The current category may not be in the user's list (e.g. an AI suggestion);
  // keep it selectable so saving doesn't silently drop it.
  const catOptions =
    category && categories && !categories.includes(category)
      ? [category, ...categories]
      : (categories ?? []);

  const save = async () => {
    if (!onSave) return;
    const patch: UpdateTransactionInput = {};
    if (category !== (tx.category ?? "")) patch.category = category;
    if (notes !== (tx.notes ?? "")) patch.notes = notes;
    setBusy(true);
    try {
      if (Object.keys(patch).length > 0) await onSave(patch);
      if (nameDirty && onRename) {
        await onRename({ merchant_raw: tx.merchant_raw, display_name: displayName.trim() });
      }
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-slate-800 py-2 last:border-b-0">
      <div className="flex items-center justify-between group">
        <button
          type="button"
          onClick={() => editable && setOpen((o) => !o)}
          className={`flex-1 text-left ${editable ? "cursor-pointer" : "cursor-default"}`}
        >
          <div className="text-sm font-medium flex items-center gap-2">
            {title}
            {tx.is_transfer === 1 && (
              <span className="text-[10px] uppercase tracking-wide rounded bg-slate-700 text-slate-300 px-1.5 py-0.5">
                Transfer
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-1">
            <span>
              {formatDate(tx.posted_at)}
              {tx.category ? ` · ${tx.category}` : ""}
            </span>
            {tx.notes && <span title={tx.notes}>· note</span>}
          </div>
        </button>
        <div className="flex items-center gap-3">
          <div className={`tabular-nums font-medium ${sign}`}>
            {formatCents(tx.amount_cents)}
          </div>
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(tx.id);
              }}
              aria-label="Delete transaction"
              className="text-slate-600 hover:text-rose-400 text-lg leading-none px-1 transition"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {editable && open && (
        <div className="mt-3 space-y-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
            <dt className="text-slate-500">Merchant</dt>
            <dd className="text-slate-300">
              {title}
              {tx.display_name && (
                <span className="block text-[11px] text-slate-600">raw: {tx.merchant_raw}</span>
              )}
            </dd>
            <dt className="text-slate-500">Amount</dt>
            <dd className="text-slate-300 tabular-nums">{formatCents(tx.amount_cents)}</dd>
            <dt className="text-slate-500">Date</dt>
            <dd className="text-slate-300">{formatDate(tx.posted_at)}</dd>
            <dt className="text-slate-500">Source</dt>
            <dd className="text-slate-300">{tx.source}</dd>
          </dl>
          <p className="text-[11px] text-slate-600">
            Amount and date come from the source and stay locked. Edit the category,
            add a note, or give this merchant a custom name.
          </p>
          {onRename && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs uppercase tracking-wide text-slate-500">Display name</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={tx.merchant_raw}
                className="rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm placeholder-slate-600"
              />
              <span className="text-[11px] text-slate-600">
                Applies to every transaction whose payee contains this merchant, past and
                future. Clearing reverts to the raw payee (or a broader rename rule, if any).
              </span>
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs uppercase tracking-wide text-slate-500">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm"
            >
              <option value="">— uncategorized —</option>
              {catOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs uppercase tracking-wide text-slate-500">Note</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Add a note…"
              className="rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm placeholder-slate-600"
            />
          </label>
          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={busy || !dirty}
              className="rounded bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-sm font-medium px-3 py-1"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
